import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import TopNav from "@/components/TopNav";
import { LOGO_URL } from "@/lib/rooted";

/**
 * Renders a legal document from a structured `sections` array of {heading, paragraphs, list?}.
 * All copy is preserved verbatim from the founder's PDF.
 */
export const LegalPage = ({ title, updated, contact, sections, testId }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div data-testid={testId} className="bg-rooted min-h-screen">
      <TopNav />

      <section className="relative pt-28 md:pt-36 pb-8 md:pb-10 overflow-hidden">
        <span
          aria-hidden
          className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full"
          style={{
            background:
              "radial-gradient(circle at center, rgba(57,151,26,0.12) 0%, rgba(57,151,26,0.04) 35%, rgba(0,0,0,0) 65%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-6 md:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-muted-rooted hover:text-[#39971a] font-display text-sm tracking-wide transition-colors duration-300"
          >
            <ArrowLeft size={16} />
            <span>Back</span>
          </Link>

          <div className="mt-14 md:mt-16">
            <img
              src={LOGO_URL}
              alt="Rooted"
              className="h-12 w-12 mb-8 drop-shadow-[0_0_30px_rgba(57,151,26,0.4)]"
            />
            <span className="verse-cite brand-green block mb-3">Legal</span>
            <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-[-0.02em] text-rooted">
              {title}
            </h1>
            <div className="verse-cite mt-6">
              BeyondBible Rooted · Last updated: {updated} · {contact}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-rooted pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-10 space-y-12">
          {sections.map((s, i) => (
            <div key={i} data-testid={`legal-section-${i}`}>
              <h2 className="font-display text-xl md:text-2xl text-rooted mb-4 tracking-[-0.01em]">
                {s.heading}
              </h2>
              <div className="space-y-4 font-body text-base md:text-[17px] leading-[1.75] text-muted-rooted">
                {s.paragraphs?.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
                {s.list && (
                  <ul className="space-y-3 pl-1">
                    {s.list.map((item, j) => (
                      <li key={j} className="flex gap-3">
                        <span className="mt-[10px] h-[1px] w-4 shrink-0 bg-[#39971a]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <footer className="bg-rooted">
        <div className="border-t border-[#f2ede6]/5">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 verse-cite flex justify-between">
            <span>© {new Date().getFullYear()} Rooted</span>
            <span className="hidden md:inline">Built by BeyondBible</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
